import React, { useMemo, useState } from 'react';
import cx from 'classnames';
import { Avatar, Box, Stack, Typography, useMediaQuery } from '@mui/material';
import TruncateMarkup from 'react-truncate-markup';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';

import config from 'config';
import useConditions from 'hooks/useConditions';

import Styles from './TravelWhenToScore.module.scss';

export interface TravelWhenToScoreProps {
  forecaster: {
    name: string;
    role: string;
    avatar: {
      url: string;
    };
  };
  highSeasonMonths: string;
  highSeasonAbility: string;
  highSeasonCrowd: string;
  highSeasonDescription: {
    json: any;
  };
  shoulderSeasonMonths: string;
  shoulderSeasonAbility: string;
  shoulderSeasonCrowd: string;
  shoulderSeasonDescription: {
    json: any;
  };
  lowSeasonMonths: string;
  lowSeasonAbility: string;
  lowSeasonCrowd: string;
  lowSeasonDescription: {
    json: any;
  };
}

const RICHTEXT_OPTIONS_TRUNCATED = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => children,
  },
};

const RICHTEXT_OPTIONS = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => (
      <Typography variant="body" color="secondary" mb={2} className={Styles.body}>
        {children}
      </Typography>
    ),
  },
};

const SeasonComponent = ({
  iconColor,
  title,
  subtitle,
  bestFor,
  crowdFactor,
  body,
  toggleTruncate,
  shouldTruncate,
}: any) => {
  const readMoreEllipsis = (
    <button type="button" onClick={toggleTruncate} className={Styles.link} aria-label="read-more">
      ... Read more
    </button>
  );
  const desktopWidth = useMediaQuery(`(min-width:${config.desktopWidth}px)`);
  const desktopXXLWidth = useMediaQuery(`(min-width:${config.desktopXXLWidth}px)`);

  let lines = 5;
  if (desktopXXLWidth) {
    lines = 4;
  } else if (desktopWidth) {
    lines = 3;
  }
  return (
    <div className={Styles.seasonWrap}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2, md: 4 }}
        justifyContent="space-between"
      >
        <div className={Styles.season}>
          <svg
            width="8"
            height="50"
            viewBox="0 0 8 50"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="8" height="50" rx="4" fill={iconColor} />
          </svg>
          <div className={Styles.forecasterInfo}>
            <Typography className={Styles.seasonTitle} component="h3" variant="title1">
              {title}
            </Typography>
            <Typography variant="subtitle2" component="p" className={Styles.seasonSubTitle}>
              {subtitle}
            </Typography>
          </div>
        </div>
        <div className={Styles.detailedWrap}>
          <Typography variant="subtitle2" component="p" className={Styles.detailed}>
            <strong>Best for: </strong>
            {bestFor}
          </Typography>
          <Typography variant="subtitle2" component="p" className={Styles.detailed}>
            <strong>Crowd factor: </strong>
            {crowdFactor}
          </Typography>
        </div>
      </Stack>
      <Box sx={{ width: '100%' }} className={Styles.body}>
        <Stack spacing={2}>
          <div>
            {shouldTruncate ? (
              <TruncateMarkup lines={lines} ellipsis={readMoreEllipsis}>
                <Typography variant="body" color="secondary" className={Styles.body}>
                  {documentToReactComponents(body.json, RICHTEXT_OPTIONS_TRUNCATED)}
                </Typography>
              </TruncateMarkup>
            ) : (
              <div>
                {documentToReactComponents(body.json, RICHTEXT_OPTIONS)}
                <button
                  type="button"
                  onClick={toggleTruncate}
                  className={cx(Styles.link, Styles.readLess)}
                  aria-label="read-less"
                >
                  Read less
                </button>
              </div>
            )}
          </div>
        </Stack>
      </Box>
    </div>
  );
};

export const TravelWhenToScore: React.FC<TravelWhenToScoreProps> = ({
  forecaster,
  highSeasonMonths,
  highSeasonAbility,
  highSeasonCrowd,
  highSeasonDescription,
  shoulderSeasonMonths,
  shoulderSeasonAbility,
  shoulderSeasonCrowd,
  shoulderSeasonDescription,
  lowSeasonMonths,
  lowSeasonAbility,
  lowSeasonCrowd,
  lowSeasonDescription,
}) => {
  const [shouldTruncateHigh, setShouldTruncateHigh] = useState(true);
  const [shouldTruncateShoulder, setShouldTruncateShoulder] = useState(true);
  const [shouldTruncateLow, setShouldTruncateLow] = useState(true);
  const { conditions } = useConditions();

  const seasonColors = useMemo(
    () => ({
      prime: conditions.find((condition) => condition.key === 'GOOD')?.color,
      shoulder: conditions.find((condition) => condition.key === 'FAIR')?.color,
      low: conditions.find((condition) => condition.key === 'POOR')?.color,
    }),
    [conditions],
  );

  const toggleTruncateHigh = () => {
    setShouldTruncateHigh(!shouldTruncateHigh);
  };

  const toggleTruncateShoulder = () => {
    setShouldTruncateShoulder(!shouldTruncateShoulder);
  };
  const toggleTruncateLow = () => {
    setShouldTruncateLow(!shouldTruncateLow);
  };

  if (
    !forecaster ||
    !highSeasonMonths ||
    !highSeasonAbility ||
    !highSeasonCrowd ||
    !highSeasonDescription ||
    !shoulderSeasonMonths ||
    !shoulderSeasonAbility ||
    !shoulderSeasonCrowd ||
    !shoulderSeasonDescription ||
    !lowSeasonMonths ||
    !lowSeasonAbility ||
    !lowSeasonCrowd ||
    !lowSeasonDescription
  )
    return <div />;

  return (
    <section
      className={cx(Styles.whenToScore, 'sl-section-container')}
      data-testid="travel-zone-when-to-score"
      id="when-to-score"
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2, md: 4 }}
        justifyContent="space-between"
        mb="20px"
      >
        <Typography variant="title2">When To Score</Typography>
        <div className={Styles.forecaster}>
          <Avatar alt={forecaster?.name} src={forecaster?.avatar?.url} className={Styles.avatar} />
          <div className={Styles.forecasterInfo}>
            <Typography variant="headline" component="p" className={Styles.forecasterName}>
              {forecaster?.name}
            </Typography>
            <Typography variant="body" className={Styles.forecasterBody}>
              {forecaster?.role}
            </Typography>
          </div>
        </div>
      </Stack>
      <SeasonComponent
        iconColor={seasonColors.prime}
        title="Prime Surf Season"
        subtitle={highSeasonMonths}
        bestFor={highSeasonAbility}
        crowdFactor={highSeasonCrowd}
        body={highSeasonDescription}
        toggleTruncate={toggleTruncateHigh}
        shouldTruncate={shouldTruncateHigh}
      />
      <SeasonComponent
        iconColor={seasonColors.shoulder}
        title="Shoulder Surf Season"
        subtitle={shoulderSeasonMonths}
        bestFor={shoulderSeasonAbility}
        crowdFactor={shoulderSeasonCrowd}
        body={shoulderSeasonDescription}
        toggleTruncate={toggleTruncateShoulder}
        shouldTruncate={shouldTruncateShoulder}
      />
      <SeasonComponent
        iconColor={seasonColors.low}
        title="Low Surf Season"
        subtitle={lowSeasonMonths}
        bestFor={lowSeasonAbility}
        crowdFactor={lowSeasonCrowd}
        body={lowSeasonDescription}
        toggleTruncate={toggleTruncateLow}
        shouldTruncate={shouldTruncateLow}
      />
    </section>
  );
};

export default TravelWhenToScore;
